/**
 * @module Card
 * @author: UI architecture team @ZS
 */

import ComponentMixin from '../smart/component.m.js';

class Base extends HTMLElement {
    constructor(...args) {
        super(...args);
    }
}

/**
 * Card component
 * @extends HTMLElement
 * @example <zs-card class="zs-card"></zs-card>
 * @example let el = document.createElement('zs-card')
 */
export default class Card extends ComponentMixin(Base) {
    constructor(...args) {
        // Constructor caveat https://github.com/WebReflection/document-register-element/
        const _ = super(...args);
        return _;
    }

    setup() {
        super.setup();

        this.syncProp('filter');
        this.syncProp('flippable');
        this.syncProp('flipped');
        this.syncProp('disabled');
        this.syncProp('nonClickable');

        this.on(['click'], undefined, undefined, false, true);
        this.on(['render', 'flip']);

    }

    /**
     * Renders the card if required and fires the render event.
     */
    render() {
        if (!this.innerHTML && this.flippable) {
            this.innerHTML = this.template();
        }
        if (this.flippable) {
            window.requestAnimationFrame(() => {
                //We need to apply the above CSS to have impact of transition only after our component gets rendered : ZSUI-845
                this.frontContainer.style.transition = "transform ease 1s";
                this.backContainer.style.transition = "transform ease 1s";
            })
        }
        this.fire('render');
    }
    /**
     * Returns the template of flippable card required to put inside the card 
     */
    template() {
        return `<div flip-card-container><div front></div><div back></div></div>`;
    }
    /**
     * Container containing the flippable front and back container of flippable card.
     * @returns {HTMLDivElement} Reference to the container of flippable card.
     */
    get flipCardContainer() {
        return this.querySelector('[flip-card-container]');
    }
    /**
     * Front container of flippable card.
     * @returns {HTMLDivElement} Reference to the front container.
     */
    get frontContainer() {
        return this.querySelector('[front]');
    }
    /**
     * Back container of flippable card.
     * @returns {HTMLDivElement} Reference to the back container.
     */
    get backContainer() {
        return this.querySelector('[back]');
    }

    static get observedAttributes() {
        return super.observedAttributes.concat(['filter', 'flippable', 'flipped', 'non-clickable', 'disabled']);
    }

    onrender() {

    }

    getPropertyType(name) {
        switch (name) {
            case 'filter':
                return 'string'
            case 'flippable':
            case 'flipped':
            case 'non-clickable':
            case 'disabled':
                return 'boolean'
            default:
                return 'string';
        }
    }

    attributeChangedCallback(name, oldValue, newValue) {
        super.attributeChangedCallback(name, oldValue, newValue);

        this.syncAttr(name, newValue, this.getPropertyType(name));
    }

    propertyChangedCallback(name, oldValue, newValue) {

        if (!this.parentNode && !this.innerHTML) {
            return;
        }
        if (name == 'flippable' && this.backContainer) {
            if (!newValue && this.flipCardContainer) {
                this.backContainer.style.display = 'none';
            }
            else {
                this.backContainer.style.display = '';
            }
        }

    }

    onclick(e) {
        if (e.target.getAttribute('role') == 'flip') {
            this.flipCard();
        }
        else if (e.currentTarget == this && this.hasAttribute('filter')) {
            this.filter = this.filter == "on" ? "off" : "on";
        }
    }
    /**
     * Flips the card when called.
    */
    flipCard() {
        var checkFlip = (this.getAttribute('flipped')) !== null;
        var isFlippable = this.hasAttribute('flippable');
        if ((isFlippable) && !(checkFlip)) {
            this.flipped = true;
        }
        else if ((isFlippable) && (checkFlip)) {
            this.flipped = false;
        }
        this.fire('flip');
    }
    cleanup() {

        this.off(['click'], this, this);

        while (this.firstChild) {
            this.removeChild(this.firstChild);
        }
    }

    connectedCallback() {
        super.connectedCallback();
        window.requestAnimationFrame(() => {
            this.render();
        })
    }
}

Card.is = 'zs-card';